import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["as", "align", "className", "children", "label", "description", "enterDelayMs", "leaveDelayMs", "defaultOpen", "closeOnActivation", "dropShadow", "highContrast"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Popover, PopoverContent } from '../Popover/index.js';
import { Escape, Enter, Space } from '../../internal/keyboard/keys.js';
import { match } from '../../internal/keyboard/match.js';
import { useDelayedState } from '../../internal/useDelayedState.js';
import { useId } from '../../internal/useId.js';
import { useNoInteractiveChildren } from '../../internal/useNoInteractiveChildren.js';
import { usePrefix } from '../../internal/usePrefix.js';
import useIsomorphicEffect from '../../internal/useIsomorphicEffect.js';

/**
 * Event types that trigger a "drag" to stop.
 */
var DRAG_STOP_EVENT_TYPES = new Set(['mouseup', 'touchend', 'touchcancel']);
var Tooltip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _child$props;
  var as = _ref.as,
    _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'top' : _ref$align,
    customClassName = _ref.className,
    children = _ref.children,
    label = _ref.label,
    description = _ref.description,
    _ref$enterDelayMs = _ref.enterDelayMs,
    enterDelayMs = _ref$enterDelayMs === void 0 ? 100 : _ref$enterDelayMs,
    _ref$leaveDelayMs = _ref.leaveDelayMs,
    leaveDelayMs = _ref$leaveDelayMs === void 0 ? 300 : _ref$leaveDelayMs,
    _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    _ref$closeOnActivatio = _ref.closeOnActivation,
    closeOnActivation = _ref$closeOnActivatio === void 0 ? false : _ref$closeOnActivatio,
    _ref$dropShadow = _ref.dropShadow,
    dropShadow = _ref$dropShadow === void 0 ? false : _ref$dropShadow,
    _ref$highContrast = _ref.highContrast,
    highContrast = _ref$highContrast === void 0 ? true : _ref$highContrast,
    rest = _objectWithoutProperties(_ref, _excluded);
  var tooltipRef = useRef(null);
  var _useDelayedState = useDelayedState(defaultOpen),
    _useDelayedState2 = _slicedToArray(_useDelayedState, 2),
    open = _useDelayedState2[0],
    setOpen = _useDelayedState2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDragging = _useState2[0],
    setIsDragging = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    focusByMouse = _useState4[0],
    setFocusByMouse = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isPointerIntersecting = _useState6[0],
    setIsPointerIntersecting = _useState6[1];
  var id = useId('tooltip');
  var prefix = usePrefix();
  var child = React.Children.only(children);
  var _ref2 = (_child$props = child === null || child === void 0 ? void 0 : child.props) !== null && _child$props !== void 0 ? _child$props : {},
    ariaLabel = _ref2['aria-label'],
    ariaLabelledBy = _ref2['aria-labelledby'],
    ariaDescribedBy = _ref2['aria-describedby'];
  var hasLabel = !!label;
  var hasAriaLabel = typeof ariaLabel === 'string' ? ariaLabel.trim() !== '' : false;

  // An `aria-label` takes precedence over `aria-describedby`, but when it's
  // needed and the user doesn't specify one, the fallback `id` is used.
  var labelledBy = hasAriaLabel ? undefined : hasLabel ? ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : id : undefined;

  // If `aria-label` is present, use any provided `aria-describedby`.
  // If not, fallback to child's `aria-describedby` or the tooltip `id` if needed.
  var describedBy = hasAriaLabel ? ariaDescribedBy : ariaDescribedBy !== null && ariaDescribedBy !== void 0 ? ariaDescribedBy : !hasLabel && !ariaLabelledBy ? id : undefined;
  var triggerProps = {
    onFocus: function onFocus() {
      return !focusByMouse && setOpen(true);
    },
    onBlur: function onBlur() {
      setOpen(false);
      setFocusByMouse(false);
    },
    onClick: function onClick() {
      return closeOnActivation && setOpen(false);
    },
    // This should be placed on the trigger in case the element is disabled
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onMouseDown: onMouseDown,
    onMouseMove: onMouseMove,
    onTouchStart: onDragStart,
    'aria-labelledby': labelledBy,
    'aria-describedby': describedBy
  };
  function getChildEventHandlers(childProps) {
    var eventHandlerFunctions = Object.keys(triggerProps).filter(function (prop) {
      return prop.startsWith('on');
    });
    var eventHandlers = {};
    eventHandlerFunctions.forEach(function (functionName) {
      eventHandlers[functionName] = function (evt) {
        triggerProps[functionName](evt);
        if (childProps !== null && childProps !== void 0 && childProps[functionName]) {
          childProps === null || childProps === void 0 ? void 0 : childProps[functionName](evt);
        }
      };
    });
    return eventHandlers;
  }
  var onKeyDown = useCallback(function (event) {
    if (open && match(event, Escape)) {
      event.stopPropagation();
      setOpen(false);
    }
    if (open && closeOnActivation && (match(event, Enter) || match(event, Space))) {
      setOpen(false);
    }
  }, [closeOnActivation, open, setOpen]);
  useIsomorphicEffect(function () {
    if (!open) {
      return undefined;
    }
    function handleKeyDown(event) {
      if (match(event, Escape)) {
        onKeyDown(event);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onKeyDown]);
  function onMouseEnter() {
    // Interactive Tags should not support onMouseEnter
    if (!(rest !== null && rest !== void 0 && rest.onMouseEnter)) {
      setIsPointerIntersecting(true);
      setOpen(true, enterDelayMs);
    }
  }
  function onMouseDown() {
    setFocusByMouse(true);
    onDragStart();
  }
  function onMouseLeave() {
    setIsPointerIntersecting(false);
    if (isDragging) {
      return;
    }
    setOpen(false, leaveDelayMs);
  }
  function onMouseMove(evt) {
    if (evt.buttons === 1) {
      setIsDragging(true);
    } else {
      setIsDragging(false);
    }
  }
  function onDragStart() {
    setIsDragging(true);
  }
  var onDragStop = useCallback(function () {
    setIsDragging(false);
    // Close the tooltip, unless the mouse pointer is within the bounds of the
    // trigger.
    if (!isPointerIntersecting) {
      setOpen(false, leaveDelayMs);
    }
  }, [isPointerIntersecting, leaveDelayMs, setOpen]);
  useNoInteractiveChildren(tooltipRef, 'The Tooltip component must have no interactive content rendered by the' + '`label` or `description` prop');
  useEffect(function () {
    if (isDragging) {
      // Register drag stop handlers.
      DRAG_STOP_EVENT_TYPES.forEach(function (eventType) {
        document.addEventListener(eventType, onDragStop);
      });
    }
    return function () {
      DRAG_STOP_EVENT_TYPES.forEach(function (eventType) {
        document.removeEventListener(eventType, onDragStop);
      });
    };
  }, [isDragging, onDragStop]);
  return /*#__PURE__*/React.createElement(Popover, _extends({
    as: as,
    ref: ref
  }, rest, {
    align: align,
    className: cx("".concat(prefix, "--tooltip"), customClassName),
    dropShadow: dropShadow,
    highContrast: highContrast,
    onKeyDown: onKeyDown,
    onMouseLeave: onMouseLeave,
    open: open
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--tooltip-trigger__wrapper")
  }, typeof child !== 'undefined' ? /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, triggerProps), getChildEventHandlers(child.props))) : null), /*#__PURE__*/React.createElement(PopoverContent, {
    "aria-hidden": open ? 'false' : 'true',
    className: "".concat(prefix, "--tooltip-content"),
    id: id,
    onMouseEnter: onMouseEnter,
    role: "tooltip"
  }, label || description));
});
export { Tooltip };